import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Result,
  Space,
  Spin,
  Tag,
  Timeline,
  Typography,
} from "antd";
import {
  COLLECTIONS,
  activeDeliveryItems,
  formatNok,
  transactionDeliveryLink,
  type Delivery,
  type Transaction,
} from "@gull/shared";
import { ErrorAlert, PageStack } from "@gull/ui";
import { useAuth } from "../lib/auth";
import { useProfile } from "../lib/useProfile";
import { db } from "../lib/firebase";
import { subscribeDelivery } from "../lib/deliveries";

const { Title, Paragraph, Text } = Typography;

const STATUS_TONE: Record<Transaction["status"], string> = {
  PENDING: "blue",
  ACCEPTED: "geekblue",
  WAITING_FOR_PAYOUT: "gold",
  SENT_TO_FINAGO: "gold",
  PAID: "green",
  CANCELLED: "default",
};

const STATUS_LABEL: Record<Transaction["status"], string> = {
  PENDING: "Opprettet",
  ACCEPTED: "Godkjent",
  WAITING_FOR_PAYOUT: "Venter på utbetaling",
  SENT_TO_FINAGO: "Sendt til utbetaling",
  PAID: "Utbetalt",
  CANCELLED: "Avbrutt",
};

function maskBankAccount(value: string | null | undefined): string {
  if (!value) return "—";
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 4) return value;
  return `•••• •• ${digits.slice(-5)}`;
}

export default function CustomerTransactionDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, loading, signInWithVipps } = useAuth();
  const [tx, setTx] = useState<Transaction | null | undefined>(undefined);
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const profile = useProfile(user?.uid);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !user) return;
    return onSnapshot(
      doc(db, COLLECTIONS.transactions, id),
      (snap) => {
        const data = snap.exists() ? (snap.data() as Transaction) : null;
        if (data && data.customerUid !== user.uid) {
          setTx(null);
          setError("Denne utbetalingen tilhører en annen konto.");
          return;
        }
        setTx(data);
      },
      (err) => setError(err.message),
    );
  }, [id, user]);

  // Keyed on the two fields that decide which delivery to watch, not on tx itself: tx is a live
  // snapshot that gets a new identity on every write, so depending on it would tear down and rebuild
  // this subscription on every unrelated transaction update.
  useEffect(() => {
    const link = tx ? transactionDeliveryLink(tx) : null;
    if (!link) {
      setDelivery(null);
      return;
    }
    return subscribeDelivery(link.deliveryMethod, link.id, setDelivery, (err) =>
      setError(err.message),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tx?.storeCheckinId, tx?.mailOrderId]);

  const items = activeDeliveryItems(delivery);

  const timelineItems = useMemo(() => {
    if (!tx) return [];
    const entries = [
      {
        color: "blue",
        children: `Utbetaling opprettet ${new Date(tx.createdAt).toLocaleString("nb-NO", {
          timeZone: "Europe/Oslo",
          dateStyle: "medium",
          timeStyle: "short",
        })}`,
      },
    ];
    if (tx.acceptedAt) {
      entries.push({
        color: "geekblue",
        children: `Godkjent ${new Date(tx.acceptedAt).toLocaleString("nb-NO", {
          timeZone: "Europe/Oslo",
          dateStyle: "medium",
          timeStyle: "short",
        })}`,
      });
    }
    if (tx.sentToFinagoAt) {
      entries.push({
        color: "gold",
        children: `Sendt til utbetaling ${new Date(tx.sentToFinagoAt).toLocaleString("nb-NO", {
          timeZone: "Europe/Oslo",
          dateStyle: "medium",
          timeStyle: "short",
        })}`,
      });
    } else if (tx.status === "WAITING_FOR_PAYOUT") {
      entries.push({
        color: "gold",
        children: "Venter på manuell utbetaling",
      });
    }
    if (tx.paidAt) {
      entries.push({
        color: "green",
        children: `Utbetalt ${new Date(tx.paidAt).toLocaleString("nb-NO", {
          timeZone: "Europe/Oslo",
          dateStyle: "medium",
          timeStyle: "short",
        })}`,
      });
    }
    if (tx.cancelledAt) {
      entries.push({
        color: "default",
        children: `Avbrutt ${new Date(tx.cancelledAt).toLocaleString("nb-NO", {
          timeZone: "Europe/Oslo",
          dateStyle: "medium",
          timeStyle: "short",
        })}`,
      });
    }
    return entries;
  }, [tx]);

  if (!id) return null;

  if (loading) {
    return (
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "32px 24px" }}>
        <Spin />
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ maxWidth: 560, margin: "64px auto", padding: 24 }}>
        <Result
          status="info"
          title="Logg inn for å se utbetalingen"
          extra={
            <Button className="vipps-button" type="primary" onClick={() => signInWithVipps()}>
              Logg inn med Vipps
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "32px 24px 64px" }}>
      <PageStack>
        <Link to="/min-side">← Tilbake til Min side</Link>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            Utbetaling
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {id}
          </Paragraph>
        </div>

        <ErrorAlert error={error} />

        {tx === undefined ? <Spin size="large" /> : null}

        {tx === null ? <Alert type="error" showIcon message="Fant ikke utbetalingen" /> : null}

        {tx ? (
          <>
            <Card>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 16,
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <Text type="secondary">Status</Text>
                  <div style={{ marginTop: 8 }}>
                    <Tag color={STATUS_TONE[tx.status]}>{STATUS_LABEL[tx.status]}</Tag>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Text type="secondary">Beløp</Text>
                  <Title level={3} style={{ margin: "8px 0 0" }}>
                    {formatNok(tx.totalAmount)}
                  </Title>
                </div>
              </div>
            </Card>

            <Card title="Detaljer">
              <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                <Descriptions.Item label="Opprettet">
                  {new Date(tx.createdAt).toLocaleString("nb-NO", {
                    timeZone: "Europe/Oslo",
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </Descriptions.Item>
                <Descriptions.Item label="Antall gjenstander">{items.length}</Descriptions.Item>
                <Descriptions.Item label="Kontonummer">
                  {maskBankAccount(tx.customerBankAccount ?? profile?.bankAccountNumber)}
                </Descriptions.Item>
                <Descriptions.Item label="Referanse">
                  {tx.finagoReference ?? "Tildeles ved utbetaling"}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Fremdrift">
              <Timeline items={timelineItems} />
            </Card>

            <Card title={`Gjenstander (${items.length})`}>
              {items.length === 0 ? (
                <Empty description="Ingen gjenstander funnet." />
              ) : (
                <Space direction="vertical" size="middle" style={{ display: "flex" }}>
                  {items.map((item) => (
                    <Card key={item.id} size="small">
                      <Text strong>{item.description ?? "Uten beskrivelse"}</Text>
                    </Card>
                  ))}
                </Space>
              )}
            </Card>
          </>
        ) : null}
      </PageStack>
    </div>
  );
}
